import { useNavigate, useParams } from "react-router-dom";
import BackIcon from "../../../assets/icons/BackIcon";

import { Avatar, Button, Dropdown, Space } from "antd";
import { useEffect, useState } from "react";
import CommentIcon from "../../../assets/icons/CommentIcon";
import DeleteIcon from "../../../assets/icons/DeleteIcon";
import LoveIcon from "../../../assets/icons/LoveIcon";
import AddCommentModal from "../../../components/Forum/AddCommentModal";
import { IMAGE_URL } from "../../../constants/constants";
import useModal from "../../../hooks/useModal";
import { disableTodayAndFutureDates } from "../../../lib/time";
import { socketService } from "../../../services/socketService";
import {
  singleCommunityForumAsync,
  toggleLikeAsync,
} from "../../../store/features/communityForum/communityForumApi";
import { useAppDispatch, useAppSelector } from "../../../store/store";
import { getImageUrl } from "../../../utils/imageUrl";

interface LikeButtonProps {
  communityForumId: string;
  commentId: string;
  replyId?: string;
  likesCount: number;
}

interface Comment {
  _id: string;
  content: string;
  userId: {
    _id: string;
    fullName: string;
    mobile?: string;
    designation?: string;
    profilePicture?: string;
  };
  replies: Reply[];
  mentions: any[];
  likesBy: any[];
  createdAt?: string;
}

interface Reply {
  _id: string;
  content: string;
  userId: {
    _id: string;
    fullName: string;
  };
  mentions: any[];
  likesBy: any[];
  createdAt?: string;
}

export default function ForumDetails() {
  const { id } = useParams();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { singleCommunityForumData } = useAppSelector(
    (state) => state.communityForum
  );
  const [localComments, setLocalComments] = useState<Comment[]>([]);

  // Sync local state with server data
  useEffect(() => {
    setLocalComments(singleCommunityForumData?.communityForumComments || []);
  }, [singleCommunityForumData]);

  // Socket listener
  useEffect(() => {
    const socket = socketService.getSocket();
    if (socket) {
      socket.on(
        "updateCommentsCommunityForum",
        (data: {
          type: string;
          comment?: any;
          reply?: any;
          commentId?: string;
        }) => {
          if (
            data.type === "newReplyCommunityForum" &&
            data.reply &&
            data.commentId
          ) {
            setLocalComments((prev) =>
              prev.map((comment) =>
                comment._id === data.commentId
                  ? { ...comment, replies: [...comment.replies, data.reply] }
                  : comment
              )
            );
          } else if (data.comment) {
            setLocalComments((prev) => [...prev, data.comment]);
          }
        }
      );
    }

    return () => {
      socket?.off("updateCommentsCommunityForum");
    };
  }, []);

  const handleLocalComment = (content: string, userId: string) => {
    const tempComment: Comment = {
      _id: `temp-${Date.now()}`,
      content,
      userId: {
        _id: userId,
        fullName: "You",
      },
      replies: [],
      mentions: [],
      likesBy: [],
    };
    setLocalComments((prev) => [...prev, tempComment]);
    return tempComment;
  };

  const handleLocalReply = (
    commentId: string,
    content: string,
    userId: string
  ) => {
    const tempReply: Reply = {
      _id: `temp-${Date.now()}`,
      content,
      userId: {
        _id: userId,
        fullName: "You",
      },
      mentions: [],
      likesBy: [],
    };

    setLocalComments((prev) =>
      prev.map((comment) =>
        comment._id === commentId
          ? { ...comment, replies: [...comment.replies, tempReply] }
          : comment
      )
    );
  };

  //dispatch
  useEffect(() => {
    dispatch(
      singleCommunityForumAsync({
        params: {
          id: id + "",
        },
      })
    );
  }, [dispatch, id]);

  useEffect(() => {
    // Join the community forum room when component mounts
    const authData = localStorage.getItem("ACTION-AID-ADMIN-AUTH-LOGIN");
    const userId = JSON.parse(authData || "{}")?.data?._id;

    if (id && userId) {
      socketService.joinCommunityForum(userId, id);
    }

    // Listen for updates
    const socket = socketService.getSocket();
    if (socket) {
      socket.on("updateCommentsCommunityForum", (data: { type: string }) => {
        if (data.type === "newReplyCommunityForum") {
          // Refresh forum data to get new reply
          dispatch(
            singleCommunityForumAsync({
              params: { id: id + "" },
            })
          );
        } else {
          // Refresh forum data to get new comment
          dispatch(
            singleCommunityForumAsync({
              params: { id: id + "" },
            })
          );
        }
      });
    }

    return () => {
      // Cleanup socket listeners
      socket?.off("updateCommentsCommunityForum");
    };
  }, [dispatch, id]);

  const shouldShowComments = () => {
    const status =
      singleCommunityForumData?.communityForumStatus?.toLowerCase();
    return status !== "pending" && status !== "draft";
  };

  // Add new function to check if commenting is allowed
  const canAddNewComments = () => {
    const isLocked = singleCommunityForumData?.isLocked;
    const isRestricted = singleCommunityForumData?.isCommentRestriction;

    // If forum is locked or comments are restricted, no new comments allowed
    return !isLocked && !isRestricted;
  };

  return (
    <div>
      <div className="flex gap-3 text-[#5E718D] items-center mb-4">
        <div className="cursor-pointer" onClick={() => navigate(-1)}>
          <BackIcon />
        </div>
        <div className="text-2xl">Topic Details</div>
      </div>

      <div className="rounded-lg bg-white p-4">
        {/* Post Title and Meta */}
        <div className="mb-1">
          <h2 className="text-xl font-semibold text-[#343A40] mb-1">
            {singleCommunityForumData?.communityForumTitle || "title"}
          </h2>
          <div className="flex gap-2 items-center mb-4">
            <span className="w-3 h-3 bg-[#EA868F]"></span>
            <span>
              {singleCommunityForumData?.categoryId?.categoryName ||
                "category name"}
            </span>
            <span className="mx-2">•</span>
            <span
              className={`status-${singleCommunityForumData?.communityForumStatus?.toLowerCase()}`}
            >
              {singleCommunityForumData?.communityForumStatus || "N/A"}
            </span>
          </div>

          <Dropdown
            dropdownRender={() => (
              <ProfilePopupCard
                profileData={singleCommunityForumData?.communityForumCreatedBy}
              />
            )}
          >
            <div className="text-sm text-[#343A40] flex items-center">
              <Avatar
                src={
                  IMAGE_URL +
                  singleCommunityForumData?.communityForumCreatedBy
                    ?.profilePicture
                }
                className="mr-2"
              />
              <div className="text-[#495057] flex items-center justify-between w-full">
                <div>
                  <Space className="cursor-pointer">
                    <span>
                      {singleCommunityForumData?.communityForumCreatedBy
                        ?.fullName || "Name"}
                    </span>
                  </Space>
                </div>
                <span>
                  {disableTodayAndFutureDates(
                    singleCommunityForumData?.updatedAt || "date"
                  )}
                </span>
              </div>
            </div>
          </Dropdown>
        </div>

        {/* Post Content */}
        {/* <div className="mb-4 ml-10">
          <p className="text-[#343A40] mb-2">
            {singleCommunityForumData?.communityForumContent}
          </p>
        </div> */}
        <div
          className="text-[#343a40] text-base leading-7"
          dangerouslySetInnerHTML={{
            __html: singleCommunityForumData?.communityForumContent || "",
          }}
        />

        {/* Post Actions */}
        <div className="flex items-center gap-4 ml-10">
          {shouldShowComments() && (
            <>
              {canAddNewComments() ? (
                <CommentButton handleLocalComment={handleLocalComment} />
              ) : (
                localComments.length > 0 && (
                  <Button
                    type="link"
                    className="flex items-center gap-2 p-0 text-[#6C757D] cursor-not-allowed opacity-60"
                    disabled
                  >
                    <CommentIcon />
                    <span>Comments Restricted</span>
                  </Button>
                )
              )}
            </>
          )}
          {/* <Button
            type="link"
            className="flex items-center gap-2 p-0 text-[#6C757D]"
          >
            <LinkIcon />
            <span>Link Copy</span>
          </Button>
          <Button
            type="link"
            className="flex items-center gap-2 p-0 text-[#6C757D]"
          >
            <BookmarkIcon />
            <span>Bookmark</span>
          </Button> */}
        </div>

        {/* Comments Section */}
        {shouldShowComments() && (
          <div className="mt-4 ml-10">
            <h3 className="text-sm font-semibold text-[#343A40] bg-[#E9ECEF] px-2">
              {singleCommunityForumData?.communityForumComments?.reduce(
                (total, comment) => total + 1 + comment.replies.length,
                0
              ) || 0}{" "}
              Comments
              {!canAddNewComments() && (
                <span className="text-[#6C757D] text-xs ml-2">
                  (Restricted)
                </span>
              )}
            </h3>
            <div className="mt-4 space-y-4">
              {/* Comment */}

              {localComments.map((val, idx) => (
                <div key={val._id} className="space-y-4">
                  {/* Main Comment */}
                  <div className="flex items-start gap-3">
                    <Avatar size="small" />
                    <div className="flex-1">
                      <div className="text-gray-800">
                        <span className="font-semibold">
                          {val?.userId?.fullName}
                        </span>
                        <div
                          className="text-[#343a40] text-base leading-7"
                          dangerouslySetInnerHTML={{
                            __html: val?.content,
                          }}
                        />
                        {/* <p>
                          <span>
                            <EnterIcon />
                          </span>{" "}
                          {val?.content}
                          
                        </p> */}
                      </div>
                      <div className="text-sm mt-1 flex items-center gap-4">
                        {canAddNewComments() && (
                          <ReplyButton
                            commentId={val?._id}
                            handleLocalReply={handleLocalReply}
                          />
                        )}
                        <LikeButton
                          communityForumId={singleCommunityForumData?._id || ""}
                          commentId={val?._id || ""}
                          likesCount={val?.likesBy?.length || 0}
                        />
                        {/* <DeleteCommentButton /> */}
                      </div>
                    </div>
                  </div>

                  {/* Replies */}
                  <div className="ml-12">
                    {val?.replies?.map((reply) => (
                      <div
                        key={reply._id}
                        className="flex items-start gap-3 mt-2"
                      >
                        <Avatar size="small" />
                        <div className="flex-1">
                          <div className="text-gray-800">
                            <span className="font-semibold">
                              {reply?.userId?.fullName}
                            </span>
                            {/* <p>{reply?.content}</p> */}
                            <div
                              className="text-[#343a40] text-base leading-7"
                              dangerouslySetInnerHTML={{
                                __html: reply?.content,
                              }}
                            />
                          </div>
                          <div className="text-sm mt-1 flex items-center gap-4">
                            <LikeButton
                              communityForumId={
                                singleCommunityForumData?._id || ""
                              }
                              commentId={val?._id || ""}
                              replyId={reply?._id}
                              likesCount={reply?.likesBy?.length || 0}
                            />
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function ProfilePopupCard({ profileData }: any) {
  return (
    <div className="flex gap-3 p-4 bg-white shadow-lg rounded-lg w-[345px]">
      <div>
        <Avatar src={getImageUrl(profileData?.profilePicture)} size="large" />
      </div>
      <div>
        <div className="text-[#212529] text-lg">
          {profileData?.fullName || "Name:"}
        </div>
        <div className="text-[#495057] text-sm">
          {profileData?.designation || "Designation:"}
        </div>
        <div className="text-sm text-[#6C757D]">
          {profileData?.mobile || "Mobile:"}
        </div>
      </div>
    </div>
  );
}

function LikeButton({
  communityForumId,
  commentId,
  replyId,
  likesCount = 0,
}: LikeButtonProps) {
  const dispatch = useAppDispatch();

  const handleLike = () => {
    dispatch(
      toggleLikeAsync({
        communityForumId,
        commentId,
        ...(replyId && { replyId }),
      })
    );
  };

  return (
    <Button
      type="link"
      onClick={handleLike}
      className="flex items-center gap-2 p-0 text-[#6C757D]"
    >
      <LoveIcon color={`${likesCount > 0 ? "#bc0f0f" : null}`} />
      <span>Like ({likesCount})</span>
    </Button>
  );
}

function CommentButton({
  handleLocalComment,
}: {
  handleLocalComment: (content: string, userId: string) => void;
}) {
  const { open, toggle, handleOpen } = useModal();

  return (
    <>
      <Button
        type="link"
        className="flex items-center gap-2 p-0 text-[#6C757D]"
        onClick={() => handleOpen({ type: "ADD" })}
      >
        <CommentIcon />
        <span>Comment</span>
      </Button>

      {open?.type === "ADD" && (
        <AddCommentModal
          open={open.open}
          handleClose={toggle}
          onLocalUpdate={(content, userId) =>
            handleLocalComment(content, userId)
          }
        />
      )}
    </>
  );
}

function ReplyButton({
  commentId,
  handleLocalReply,
}: {
  commentId: string;
  handleLocalReply: (
    commentId: string,
    content: string,
    userId: string
  ) => void;
}) {
  const { open, toggle, handleOpen } = useModal();

  return (
    <>
      <Button
        type="link"
        className="flex items-center gap-2 p-0 text-[#6C757D]"
        onClick={() => handleOpen({ type: "ADD" })}
      >
        <CommentIcon />
        <span>Reply</span>
      </Button>

      {open?.type === "ADD" && (
        <AddCommentModal
          open={open.open}
          handleClose={toggle}
          commentId={commentId}
          onLocalUpdate={(content, userId) =>
            handleLocalReply(commentId, content, userId)
          }
        />
      )}
    </>
  );
}

function DeleteCommentButton() {
  return (
    <Button type="link" className="flex items-center gap-2 p-0 text-[#6C757D]">
      <DeleteIcon />
    </Button>
  );
}
